<template>
    <div>
<!-- DIALOGS -->
        <InventoriesImportDialog :alvqaContext="alvqa + '-list'" />
        <ContextHelpDialog />

        <BaseListPage
            headline="lang_ERP_INVENTORIES"
            :context="context"
            :alvqa="alvqa"
        >

        <!-- FILTERS -->
        <template v-slot:filter>

            <!-- WAREHOUSES -->
            <v-col>
                <DropdownFilter 
                    :context="context + 'Warehouses'"
                    label="lang_GENERIC_CAPTION_WAREHOUSES"
                    :items="getWarehouses"
                    :alvqa="alvqa + '-list-dropdown-warehouses'"
                />
            </v-col>

            <!-- VALUE TYPES -->
            <v-col>
                <DropdownFilter 
                    :context="context + 'ValueTypes'"
                    label="lang_ERP_INVENTORIES_DROPDOWN_VALUE_TYPES"
                    :items="getDropdownValueTypes"
                    :alvqa="alvqa + '-list-dropdown-value-types'"
                />
            </v-col>

            <!-- STOCK TO DATE -->
            <v-col>
               <BaseEditPageDatePicker 
                        field="StockToDate"
                        :context="context"
                        :label="$t('lang_ERP_INVENTORIES_CAPTION_STOCK_TO_DATE')"  
                        :alvqa="alvqa + '-datepicker-stock-to-date'"
                        :clearable="false"
                />
            </v-col>

        </template>

        <!-- DATA TABLE -->                        
        <template v-slot:dataTable>
            <BaseDataTableList 
                    :headers="headers"
                    :context="context"
                    readOnlyGhostTdSpan="1"
                    :alvqa="alvqa"
                    :skipInitialFetch="true"
                    customNoDataText="lang_ERP_INVENTORIES_MESSAGE_SELECT_DATE_AND_SEARCH"
                >
                <template v-slot:topSlot><InventoriesTopSlot /></template>
                <!-- DATA TABLE CONTENT: columns -->
                <template v-slot:body="{ item }">
                    
                    <ExpandIcon 
                        :isExpanded="item.isExpanded" 
                        :alvqa="alvqa + '-list-row-' + item.item.alvId + '-icon-expand'"
                    />

                    <td class="no_ellipsis"
                        :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-release-title'"
                    > {{ item.item.title }} </td>

                    <td class="no_ellipsis"
                        :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-performer'"
                    > {{ $alvPlugin.getArtistOrPerformerName(item.item.performer) }} </td>

                    <td class="no_ellipsis"
                        :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-ean'"
                    > {{ item.item.ean }} </td>

                    <td class="no_ellipsis"
                        :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-catalogue-number'"
                    > {{ item.item.catalogueNumber }} </td>

                    <td class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-release-date'">
                        {{ item.item.dateRetail !== null ? $d(new Date(item.item.dateRetail), 'alvDateFormat') : '' }}
                    </td>

                </template>
                        
                <!-- EXPAND -->
                <template v-slot:expandSlot="{ id }"><InventoriesReadonly :itemId="id"/></template>
                
            </BaseDataTableList>
            </template>
        </BaseListPage>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseListPage from '@/components/_base/BaseListPage'
import BaseDataTableList from '@/components/_base/BaseDataTableList'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageDatePicker from '@/components/_editForm/BaseEditPageDatePicker'
import { DropdownErpInventoriesValueTypes } from '@/assets/dropdowns/alvDropdowns.js'

import InventoriesTopSlot from './InventoriesTopSlot'
import InventoriesReadonly from './_readonly/InventoriesReadonly'

import InventoriesImportDialog from './_dialogs/InventoriesImportDialog'
import ContextHelpDialog from '@/components/_dialogs/ContextHelpDialog'

export default {
    name: "InventoriesList",
    components: {
        BaseListPage,
        BaseDataTableList,
        ExpandIcon,
        BaseEditPageDropdown,
        BaseEditPageDatePicker,
        DropdownFilter,

        InventoriesTopSlot,
        InventoriesReadonly,

        InventoriesImportDialog,
        ContextHelpDialog
    },
    data() {
        return {
            context: "Inventories",
            alvqa: "erp-inventories"
        }
    },
    computed: {
        ...mapGetters(['getInventoriesSearch']),

		/** Returns an Array with header-objects with props.
        *  @return {[{text: String, align: string, sortable: string, value: string}]} props of the header */        
       headers() {
           return [
				{
                    text: '', 
					value: 'data-table-expand',
					align: 'center',
                    sortable: false, 
					width: "48px",
				},
                {
                    text: this.$t('lang_GENERIC_CAPTION_ITEM_TITLE'), 
                    value: 'title', 
                    align: 'left', 
                    sortable: false,
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_PERFORMER'), 
                    value: 'performer', 
                    align: 'left', 
                    sortable: false,
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_EAN'), 
                    value: 'ean', 
                    align: 'left', 
                    sortable: false,
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_CATALOGUE_NUMBER'), 
                    value: 'catalogueNumber', 
                    align: 'left', 
                    sortable: false,
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_DATE_RETAIL'), 
                    value: 'dateRetail', 
                    align: 'left', 
                    sortable: false,
                },
            ]
        },
        
        getWarehouses() {
            let options = JSON.parse(JSON.stringify(this.$store.getters.getInventoriesWarehousesOptions))
            let items = []

            options.forEach(option => {
                let item = {
                    'alvqa': this.alvqa + '-list-dropdown-warehouses-' + option.alvId,
                    'text': option.gemaName + ' ('+ option.alvId +')',
                    'value': option.alvId
                }
                items.push(item)
            })
            
            return items
        },

        getDropdownValueTypes() {
            return DropdownErpInventoriesValueTypes

        },
    },

    created() {
        this.$store.dispatch('clearFilters')
        this.$store.dispatch('fetchDropdownInventoriesWarehouses')
    }
}
</script>